import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useBookingContext } from '../../../contexts/BookingContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { templatesApi } from '../../../services/api';
import { LocalTemplate, VerifyResult } from '../../../types/templates';
import { BookingConfig, DaysInfo, TimeRange } from '../types';
import {
  getTemplateCollisions,
  parseDaysInfoFromApi,
  buildDaysInfoPayload,
  parseExcludedDaysFromApi,
  buildExcludedDaysPayload,
  validateTimeRanges,
  getPerDayWrapPartners,
} from '../utils';
import { BOOKING_TEMPLATE_FIELDS } from '../constants';

const isMissingReminderGap = (value: unknown) => value === null || value === undefined || value === '';

const getInitialGlobalRanges = (config: BookingConfig): TimeRange[] => {
  if (config.global_ranges && config.global_ranges.length > 0) {
    return config.global_ranges || [];
  }

  const daySlots = config.days_info?.slots;
  if (!daySlots) {
    return config.global_ranges || [];
  }

  for (const ranges of Object.values(daySlots)) {
    if (Array.isArray(ranges) && ranges.length > 0) {
      return ranges
        .map((range) => {
          if (Array.isArray(range) && range.length >= 2) {
            return {
              start_time: String(range[0]).slice(0, 5),
              end_time: String(range[1]).slice(0, 5),
            };
          }

          if (
            range &&
            typeof range === 'object' &&
            'start_time' in range &&
            'end_time' in range
          ) {
            return {
              start_time: range.start_time,
              end_time: range.end_time,
            };
          }

          return null;
        })
        .filter((range): range is TimeRange => range !== null);
    }
  }

  return config.global_ranges || [];
};

const buildEditorStateFromConfig = (
  config: BookingConfig | null,
  isNewConfig: boolean,
): Partial<BookingConfig & {
  smb_cancellation_alert_enabled?: boolean;
  smb_reschedule_alert_enabled?: boolean;
}> => {
  if (!config) {
    return {
      first_slot_offset: 30,
      is_per_day_schedule: false,
      max_booking_window: 5760,
      allow_multiple_bookings_per_slot: false,
    };
  }

  return {
    ...config,
    global_ranges: getInitialGlobalRanges(config),
    days: (config.days && config.days.length > 0) ? config.days : [1, 2, 3, 4, 5],
    allow_reminders_to_lead: config.allow_reminders_to_lead !== undefined ? config.allow_reminders_to_lead : true,
    allow_reminders_to_smb: config.allow_reminders_to_smb !== undefined ? config.allow_reminders_to_smb : true,
    // Virtual states: Enable if template exists or if config exists
    smb_cancellation_alert_enabled: isNewConfig ? true : !!config.cancellation_notification_template_smb,
    smb_reschedule_alert_enabled: isNewConfig ? true : !!config.reschedule_notification_template_smb,
    is_per_day_schedule: config.is_per_day_schedule ?? false,
    first_slot_offset: config.first_slot_offset ?? 30,
    max_booking_window: config.max_booking_window ?? 5760,
    allow_multiple_bookings_per_slot: config.allow_multiple_bookings_per_slot ?? false,
    days_info: {
      slots: config.days_info?.slots || {},
    },
    excluded_days: {
      days: parseExcludedDaysFromApi(config.excluded_days),
    },
  };
};

export const useBookingConfigEditor = (smbId: string) => {
  const {
    config,
    fetchConfig,
    updateConfig,
    isLoading,
    isNewConfig,
    error: configError
  } = useBookingContext();
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState<Partial<BookingConfig & {
    smb_cancellation_alert_enabled?: boolean;
    smb_reschedule_alert_enabled?: boolean;
  }>>({});
  const [initialFormData, setInitialFormData] = useState<typeof formData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [validationErrorTarget, setValidationErrorTarget] = useState<{ key: string; attempt: number } | null>(null);
  
  const [localTemplates, setLocalTemplates] = useState<LocalTemplate[]>([]);
  const [activePreviewField, setActivePreviewField] = useState<string | null>('confirmation_new_appointment_template_id');
  const [templateVerifyErrors, setTemplateVerifyErrors] = useState<Record<string, VerifyResult>>({});
  const lastHydratedConfig = useRef<BookingConfig | null | undefined>(undefined);
  const lastHydratedSmbId = useRef<string | null>(null);

  // Combine backend error if any (usually for global failures)
  const error = configError;

  useEffect(() => {
    if (smbId) {
      fetchConfig();
      // Fetch local templates
      templatesApi.getTemplates({ smb_id: smbId, limit: 100 })
        .then(res => setLocalTemplates(res.templates || []))
        .catch(err => console.error('Failed to load templates', err));
    }
  }, [smbId, fetchConfig]);

  useEffect(() => {
    const smbChanged = lastHydratedSmbId.current !== smbId;
    const configChanged = lastHydratedConfig.current !== config;

    if (!smbChanged && !configChanged) return;

    const initial = buildEditorStateFromConfig(config, isNewConfig);
    setFormData(initial as any);
    setInitialFormData(initial as any);
    lastHydratedSmbId.current = smbId;
    lastHydratedConfig.current = config;
  }, [smbId, config, isNewConfig]);

  const isDirty = useMemo(() => {
    if (!initialFormData) return false;
    // Deep comparison of form fields
    return JSON.stringify(formData) !== JSON.stringify(initialFormData);
  }, [formData, initialFormData]);

  const selectedTemplatesData = useMemo(() => {
    if (!localTemplates.length) return {};
    return BOOKING_TEMPLATE_FIELDS.reduce((acc, field) => {
      const dbId = (formData as any)[field];
      acc[field] = localTemplates.find(t => t.id === dbId) || null;
      return acc;
    }, {} as Record<string, LocalTemplate | null>);
  }, [formData, localTemplates]);

  const handleChange = useCallback((field: string, value: any) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      if (field === 'allow_reminders_to_lead' && !value && isMissingReminderGap(prev.reminder_duration_gap_lead)) {
        updated.reminder_duration_gap_lead = 0;
      }
      if (field === 'allow_reminders_to_smb' && !value && isMissingReminderGap(prev.reminder_duration_gap_smb)) {
        updated.reminder_duration_gap_smb = 0;
      }

      // Re-validate range errors immediately when ranges change
      if (field === 'days_info' || field === 'global_ranges') {
        setErrors((prevErrors) => {
          // Start fresh, only keep non-range errors that are truthy
          const newErrors: Record<string, string | null> = {};
          Object.keys(prevErrors).forEach((key) => {
            if (!key.startsWith('days_info_slot_') && prevErrors[key]) {
              newErrors[key] = prevErrors[key];
            }
          });

          // Re-validate ranges with the updated formData
          const isPerDay = updated.is_per_day_schedule;
          let overrides: Record<string, TimeRange[] | null> | undefined;

          if (isPerDay) {
            overrides = updated.days_info?.slots as Record<string, TimeRange[] | null> | undefined;
          } else {
            // Inline version of getGlobalRangesForSelectedDays
            const selectedDays = updated.days || [];
            const globalRanges = (updated.global_ranges || []) as TimeRange[];
            const copiedRanges = globalRanges.map((range) => ({ ...range }));
            const slots: Record<string, TimeRange[] | null> = {};
            selectedDays.forEach((day) => {
              slots[String(day)] = copiedRanges.map((range) => ({ ...range }));
            });
            overrides = slots;
          }

          if (overrides && updated.duration) {
            // Only validate ranges for active business days
            const activeDays = updated.days || [];
            for (const [key, ranges] of Object.entries(overrides)) {
              const dayNum = Number(key);
              if (!activeDays.includes(dayNum)) continue; // Skip toggled-off days

              if (ranges && Array.isArray(ranges)) {
                const wrap = isPerDay
                  ? getPerDayWrapPartners(overrides as any, dayNum, activeDays)
                  : undefined;
                const rangeErrors = validateTimeRanges(ranges as TimeRange[], updated.duration, wrap);
                rangeErrors.forEach((err) => {
                  newErrors[`days_info_slot_${key}_${err.index}`] = err.message;
                });
              }
            }
          }

          return newErrors;
        });
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev, [field]: null };

          // Specifically clear booking limit errors if the limits object is being updated
          if (field === 'booking_limits') {
            newErrors.booking_limits_daily = null;
            newErrors.booking_limits_weekly = null;
            newErrors.booking_limits_monthly = null;
          }

          // If a toggle is disabled, clear the error for its dependent template field
          if (field === 'allow_reminders_to_lead' && !value) {
            newErrors.reminder_template_id_lead = null;
            newErrors.reminder_duration_gap_lead = null;
          }
          if (field === 'allow_reminders_to_smb' && !value) {
            newErrors.reminder_template_id_smb = null;
            newErrors.reminder_duration_gap_smb = null;
          }
          if (field === 'smb_cancellation_alert_enabled' && !value) newErrors.cancellation_notification_template_smb = null;
          if (field === 'smb_reschedule_alert_enabled' && !value) newErrors.reschedule_notification_template_smb = null;
          if (field === 'first_slot_offset') newErrors.first_slot_offset = null;
          if (field === 'max_booking_window') newErrors.max_booking_window = null;

          return newErrors;
        });
      }

      return updated;
    });
  }, []);

  const getGlobalRangesForSelectedDays = useCallback((): Record<string, TimeRange[] | null> => {
    const selectedDays = formData.days || [];
    const globalRanges = (formData.global_ranges || []) as TimeRange[];
    const copiedRanges = globalRanges.map((range) => ({ ...range }));
    const slots: Record<string, TimeRange[] | null> = {};

    selectedDays.forEach((day) => {
      slots[String(day)] = copiedRanges.map((range) => ({ ...range }));
    });

    return slots;
  }, [formData.days, formData.global_ranges]);

  const validateForm = (): boolean => {
    // Start fresh so save-time validation fully reflects the current form data.
    const newErrors: Record<string, string | null> = {};

    // 1. General Settings
    if (!formData.duration || formData.duration < 15) {
      newErrors.duration = 'Minimum duration is 15 minutes';
    } else if (formData.duration % 15 !== 0) {
      newErrors.duration = 'Duration must be a multiple of 15 minutes';
    }

    if (!formData.days || formData.days.length === 0) {
      newErrors.days = 'At least one working day is required';
    }

    // 2. Mandatory Templates
    if (!formData.confirmation_new_appointment_template_id) {
      newErrors.confirmation_new_appointment_template_id = 'Selection required';
    }
    if (!formData.cancellation_notification_template_lead) {
      newErrors.cancellation_notification_template_lead = 'Selection required';
    }
    if (!formData.reschedule_notification_template_lead) {
      newErrors.reschedule_notification_template_lead = 'Selection required';
    }

    // 3. Conditional Reminders
    if (formData.allow_reminders_to_lead) {
      if (!formData.reminder_template_id_lead) {
        newErrors.reminder_template_id_lead = 'Selection required';
      }
      if (!formData.reminder_duration_gap_lead || formData.reminder_duration_gap_lead % 15 !== 0) {
        newErrors.reminder_duration_gap_lead = 'Must be a multiple of 15';
      } else if (formData.reminder_duration_gap_lead < 15) {
        newErrors.reminder_duration_gap_lead = 'Minimum 15 mins';
      }
    }
    if (formData.allow_reminders_to_smb) {
      if (!formData.reminder_template_id_smb) {
        newErrors.reminder_template_id_smb = 'Selection required';
      }
      if (!formData.reminder_duration_gap_smb || formData.reminder_duration_gap_smb % 15 !== 0) {
        newErrors.reminder_duration_gap_smb = 'Must be a multiple of 15';
      } else if (formData.reminder_duration_gap_smb < 15) {
        newErrors.reminder_duration_gap_smb = 'Minimum 15 mins';
      }
    }

    // 4. Conditional SMB Alerts (Virtual)
    if (formData.smb_cancellation_alert_enabled && !formData.cancellation_notification_template_smb) {
      newErrors.cancellation_notification_template_smb = 'Selection required';
    }
    if (formData.smb_reschedule_alert_enabled && !formData.reschedule_notification_template_smb) {
      newErrors.reschedule_notification_template_smb = 'Selection required';
    }

    // 5. Booking Limits Hierarchy
    const limits = formData.booking_limits;
    if (limits) {
      const daily = limits.daily || 0;
      const weekly = limits.weekly || 0;
      const monthly = limits.monthly || 0;

      if (weekly < daily) {
        newErrors.booking_limits_weekly = 'Weekly limit must be ≥ Daily limit';
      }
      if (monthly < weekly) {
        newErrors.booking_limits_monthly = 'Monthly limit must be ≥ Weekly limit';
      }
    }

    // 6. Days Info — validate time ranges using shared validator
    const isPerDay = formData.is_per_day_schedule;
    const overrides = isPerDay
      ? formData.days_info?.slots
      : getGlobalRangesForSelectedDays();
    if (overrides) {
      // Only validate ranges for active business days
      const activeDays = formData.days || [];
      for (const [key, value] of Object.entries(overrides)) {
        const dayNum = Number(key);
        if (isPerDay && !activeDays.includes(dayNum)) continue; // Skip toggled-off days in per-day mode

        if (value && Array.isArray(value)) {
          // In per-day mode a slot can span midnight into the next calendar day, so pair this
          // day's 12:00 AM tail/head with the adjacent days' ranges.
          const wrap = isPerDay
            ? getPerDayWrapPartners(overrides as any, dayNum, activeDays)
            : undefined;
          const rangeErrors = validateTimeRanges(value as TimeRange[], formData.duration, wrap);
          rangeErrors.forEach((err) => {
            newErrors[`days_info_slot_${key}_${err.index}`] = err.message;
          });
        }
      }
    }

    // 7. Excluded Days — validate uniqueness and time ranges
    const excludedDays = formData.excluded_days?.days;
    if (excludedDays && excludedDays.length > 0) {
      const seenDates = new Set<string>();
      excludedDays.forEach((entry, idx) => {
        if (!entry.day) {
          newErrors[`excluded_day_${idx}_day`] = 'Date is required';
        } else if (seenDates.has(entry.day)) {
          newErrors[`excluded_day_${idx}_day`] = 'Date already excluded';
        } else {
          seenDates.add(entry.day);
        }
      });
    }

    // 8. Advanced Settings
    if (formData.first_slot_offset === null || formData.first_slot_offset === undefined) {
      newErrors.first_slot_offset = 'Required — Minimum 30 min';
    } else if (formData.first_slot_offset < 30) {
      newErrors.first_slot_offset = 'Minimum 30 min - Required';
    }

    if (formData.max_booking_window === null || formData.max_booking_window === undefined) {
      newErrors.max_booking_window = 'Required — Minimum 30 min';
    } else if (formData.max_booking_window < 30) {
      newErrors.max_booking_window = 'Minimum 30 min - Required';
    }

    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some(err => !!err);
    if (hasErrors) {
      console.log('Validation errors:', Object.entries(newErrors).filter(([_, v]) => !!v));
      const firstErrorKey = Object.keys(newErrors).find((key) => !!newErrors[key]);
      if (firstErrorKey) {
        setValidationErrorTarget({ key: firstErrorKey, attempt: Date.now() });
      }
      showNotification('Please correctly fill all the required fields', 'error');
    } else {
      setValidationErrorTarget(null);
    }
    return !hasErrors;
  };

  const handleSave = async (overrides?: Record<string, any>) => {
    if (!validateForm()) return;

    setIsSaving(true);
    setTemplateVerifyErrors({});

    // Bulk-verify all selected templates before saving
    const selectedPlatformIds: { fieldId: string; platformId: string }[] = selectedTemplates
      .filter(s => s.templateId)
      .map(s => {
        const tpl = localTemplates.find(t => t.id === s.templateId);
        return tpl?.platform_template_id ? { fieldId: s.fieldId, platformId: tpl.platform_template_id } : null;
      })
      .filter(Boolean) as { fieldId: string; platformId: string }[];

    if (selectedPlatformIds.length > 0 && smbId) {
      try {
        const verifyRes = await templatesApi.verifyTemplateStatus(smbId, selectedPlatformIds.map(x => x.platformId));
        const newErrors: Record<string, VerifyResult> = {};
        selectedPlatformIds.forEach(({ fieldId, platformId }) => {
          const r = verifyRes[platformId];
          if (r && !r.can_proceed) {
            newErrors[fieldId] = r
          }
        });
        if (Object.keys(newErrors).length > 0) {
          setTemplateVerifyErrors(newErrors);
          console.log(newErrors)
          setIsSaving(false);
          showNotification('Failed to verify templates', 'error')
          return;
        }
      } catch (err) {
        console.error('Failed to verify templates:', err);
        showNotification('Failed to verify templates', 'error')
        return
      }
    }
    const {
      smb_cancellation_alert_enabled,
      smb_reschedule_alert_enabled,
      days,
      ...rest
    } = formData;

    const payload: Record<string, any> = {
      ...rest,
      ...overrides,
      days: days || [],
    };

    if (!payload.allow_reminders_to_lead && isMissingReminderGap(payload.reminder_duration_gap_lead)) {
      payload.reminder_duration_gap_lead = 0;
    }
    if (!payload.allow_reminders_to_smb && isMissingReminderGap(payload.reminder_duration_gap_smb)) {
      payload.reminder_duration_gap_smb = 0;
    }

    // Build days_info with time ranges (convert TimeRange[] to API string[][] format)
    const scheduleSlots = rest.is_per_day_schedule
      ? ((rest.days_info?.slots || {}) as Record<string, TimeRange[] | null>)
      : getGlobalRangesForSelectedDays();

    payload.days_info = buildDaysInfoPayload(scheduleSlots, days || []);

    if (rest.excluded_days?.days) {
      payload.excluded_days = buildExcludedDaysPayload(rest.excluded_days.days);
    }

    const success = await updateConfig(payload as BookingConfig);

    if (success) {
      // Keep the just-saved values visible locally; PATCH only acknowledges success.
      const updatedFormState = {
        ...formData,
        ...overrides,
        days_info: {
          slots: parseDaysInfoFromApi(payload.days_info as DaysInfo),
        },
      };
      setFormData(updatedFormState as any);
      setInitialFormData(updatedFormState as any);
      lastHydratedConfig.current = config;
      lastHydratedSmbId.current = smbId;
      await fetchConfig();
    }

    setIsSaving(false);
  };

  const selectedTemplates = useMemo(() => [
    { fieldId: 'confirmation_new_appointment_template_id', templateId: formData.confirmation_new_appointment_template_id || null },
    { fieldId: 'reminder_template_id_lead', templateId: formData.reminder_template_id_lead || null },
    { fieldId: 'reminder_template_id_smb', templateId: formData.reminder_template_id_smb || null },
    { fieldId: 'cancellation_notification_template_lead', templateId: formData.cancellation_notification_template_lead || null },
    { fieldId: 'cancellation_notification_template_smb', templateId: formData.cancellation_notification_template_smb || null },
    { fieldId: 'reschedule_notification_template_lead', templateId: formData.reschedule_notification_template_lead || null },
    { fieldId: 'reschedule_notification_template_smb', templateId: formData.reschedule_notification_template_smb || null }
  ], [formData]);

  const collisions = useMemo(() => getTemplateCollisions(selectedTemplates), [selectedTemplates]);

  return {
    formData,
    isLoading,
    isSaving,
    isNewConfig,
    error,
    errors,
    validationErrorTarget,
    config,
    selectedTemplates,
    collisions,
    selectedTemplatesData,
    localTemplates,
    templateVerifyErrors,
    activePreviewField,
    setActivePreviewField,
    handleChange,
    handleSave,
    validateForm,
    isDirty,
    getGlobalRangesForSelectedDays,
  };
};
